import { useEffect, useState } from 'react';
import { fetchCalendarEvents, type ChurchEvent } from './lib/calendar';
import { juarezNow } from './lib/timezone';
import { services, type Service } from './site';

/** Revela elementos `.reveal` al entrar en el viewport (respeta reduced-motion). */
export function useReveal(trigger?: unknown) {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal:not(.is-visible)');
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (prefersReducedMotion) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [trigger]);
}

/** Sombrea el header al hacer scroll. */
export function useScrolled(offset = 24) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > offset);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [offset]);

  return scrolled;
}

export interface NextService {
  service: Service;
  date: Date;
  /** Texto humano: "Hoy", "Mañana" o "En N días". */
  when: string;
  /** Cuenta regresiva corta: "3 h 20 min". */
  countdown: string;
  isToday: boolean;
}

function computeNextService(): NextService {
  const now = juarezNow();
  let bestDate: Date | null = null;
  let bestService: Service = services[0];

  for (const service of services) {
    for (let offset = 0; offset < 8; offset += 1) {
      const candidate = new Date(now);
      candidate.setDate(now.getDate() + offset);
      if (candidate.getDay() !== service.weekday) continue;
      candidate.setHours(service.hour, service.minute, 0, 0);
      if (candidate.getTime() <= now.getTime()) continue;
      if (!bestDate || candidate < bestDate) {
        bestDate = candidate;
        bestService = service;
      }
      break;
    }
  }

  const date = bestDate ?? now;
  const diffMs = Math.max(0, date.getTime() - now.getTime());
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const isToday = days === 0 && date.getDate() === now.getDate();
  const isTomorrow = days === 0 || (days === 1 && date.getDate() !== now.getDate());

  let when: string;
  if (isToday) when = 'Hoy';
  else if (days === 1 || isTomorrow) when = 'Mañana';
  else when = `En ${days} días`;

  let countdown: string;
  if (days > 0 && hours > 0) countdown = `${days} d ${hours} h`;
  else if (days > 0) countdown = `${days} d`;
  else if (hours > 0) countdown = `${hours} h ${minutes} min`;
  else countdown = `${minutes} min`;

  return { service: bestService, date, when, countdown, isToday };
}

/** Próximo servicio con cuenta regresiva (hora de Ciudad Juárez), actualizado cada 30 s. */
export function useNextService(): NextService {
  const [next, setNext] = useState<NextService>(() => computeNextService());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNext(computeNextService());
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  return next;
}

export type EventsStatus = 'loading' | 'ready' | 'error';

export function useEvents() {
  const [events, setEvents] = useState<ChurchEvent[]>([]);
  const [status, setStatus] = useState<EventsStatus>('loading');

  useEffect(() => {
    let cancelled = false;

    fetchCalendarEvents()
      .then((data) => {
        if (cancelled) return;
        setEvents(data);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { events, status };
}

export type CountdownStatus = 'upcoming' | 'live' | 'ended';

export interface EventCountdownState {
  status: CountdownStatus;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function splitMs(diff: number) {
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function computeCountdown(start: Date, end: Date, now = new Date()): EventCountdownState {
  if (now.getTime() >= end.getTime()) {
    return { status: 'ended', days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  if (now.getTime() >= start.getTime()) {
    return { status: 'live', ...splitMs(end.getTime() - now.getTime()) };
  }
  return { status: 'upcoming', ...splitMs(start.getTime() - now.getTime()) };
}

/** Cuenta atrás absoluta (sirve igual desde Juárez o desde otro país). */
export function useEventCountdown(start: Date, end: Date): EventCountdownState {
  const [state, setState] = useState(() => computeCountdown(start, end));

  useEffect(() => {
    setState(computeCountdown(start, end));
    const timer = window.setInterval(() => {
      setState(computeCountdown(start, end));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [start, end]);

  return state;
}
